import { useState } from 'react';
import { Outlet, Navigate, useParams, useLocation } from 'react-router';
import { buildLoginUrl } from '../../utils/returnTo';
import { makeStyles, tokens } from '@fluentui/react-components';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useAuthStore } from '../../store/auth.store';
import { DemoGate } from '../auth/DemoGate';
import { SiteFooter } from './SiteFooter';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    height: '100vh',
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground2,
    '@media (max-width: 768px)': {
      padding: '16px 12px',
    },
  },
  contentFixed: {
    flex: 1,
    overflow: 'hidden',
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground2,
    '@media (max-width: 768px)': {
      padding: '16px 12px',
    },
  },
  /* Semi-transparent backdrop — only visible on mobile when sidebar is open */
  backdrop: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'block',
      position: 'fixed',
      inset: '0',
      backgroundColor: 'rgba(0,0,0,0.4)',
      zIndex: '99',
    },
  },
});

export function AppShell() {
  const s = useStyles();
  const { token, user } = useAuthStore();
  const { tenantId: urlTenantId } = useParams();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Si la sesión activa pertenece a otro tenant que el de la URL, la sesión
  // ya no es válida para esta ruta — forzar re-login en vez de dejar que la
  // API llame silenciosamente con el tenantId equivocado (spinner infinito).
  // OJO: la URL usa el `code` (slug, ej. "agrovid"), mientras que user.tenantId
  // es el UUID interno de DynamoDB — nunca son iguales. Comparar contra
  // tenantCode (con fallback a tenantId para tenants sin code, ej. demo).
  const sessionTenantSlug = user?.tenantCode ?? user?.tenantId;
  const tenantMismatch = !!token && !!sessionTenantSlug && sessionTenantSlug !== urlTenantId;
  const isChat = /\/chat\/?$/.test(location.pathname);

  return (
    <DemoGate>
      {!token || tenantMismatch
        ? <Navigate to={buildLoginUrl(location.pathname, location.search, urlTenantId)} replace />
        : (
          <div className={s.root}>
            {/* Backdrop closes sidebar on mobile tap-outside */}
            {sidebarOpen && (
              <div
                className={s.backdrop}
                onClick={() => setSidebarOpen(false)}
                aria-hidden
              />
            )}

            <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className={s.main}>
              <TopBar onMenuClick={() => setSidebarOpen(prev => !prev)} />
              {/* El chat ocupa exactamente el área visible: sin scroll de página ni footer, para que enfocar el texto no lo revele */}
              <main className={isChat ? s.contentFixed : s.content}>
                {/* El footer queda bajo el pliegue: aparece solo al llegar al final de la página */}
                <div style={{ minHeight: '100%' }}>
                  <Outlet />
                </div>
                {!isChat && <SiteFooter showBot />}
              </main>
            </div>
          </div>
        )
      }
    </DemoGate>
  );
}
